import type { StrikePricesState } from './types';
import { createSelector } from '@reduxjs/toolkit';
import { sortBy, sumBy } from 'es-toolkit';

/**
 * Minimal state shape the strike-prices selectors read from. Exported because
 * app-level selectors compose these selectors, so the type must be nameable
 * across the package boundary.
 */
export interface StrikePricesStore {
  loans: {
    strikePrices: StrikePricesState;
  };
}

export const selectEditStrikePriceStatus = (store: StrikePricesStore) =>
  store.loans.strikePrices.resEditStrikePrice;
export const selectAddStrikePriceStatus = (store: StrikePricesStore) =>
  store.loans.strikePrices.resAddStrikePrice;

export const selectRemoveStrikePriceStatus = (store: StrikePricesStore) =>
  store.loans.strikePrices.resRemoveStrikePrice;

export const selectSelectedStrikePrice = (state: StrikePricesStore) =>
  state.loans.strikePrices.selectedStrikePrice;

export const selectLoanStrikePrices = createSelector(
  (store: StrikePricesStore) => store.loans.strikePrices.strikePrices,
  (list) => sortBy(list ?? [], ['strikeDate', 'strikePrice']),
);

export const selectedStrikePriceRequestStatus = (store: StrikePricesStore) =>
  store.loans.strikePrices.requestStatus;
export const selectStrikePricesIsLoading = (store: StrikePricesStore) =>
  store.loans.strikePrices.requestStatus === 'pending';

export const selectStrikePricesError = (store: StrikePricesStore) =>
  store.loans.strikePrices.error?.description;

export const selectWeightedAvgTokensAmount = createSelector(
  selectLoanStrikePrices,
  (strikePrices) =>
    sumBy(
      strikePrices.filter((item) => item.strikePrice > 0),
      (item) => item.tokensAmount,
    ),
);

export const selectWeightedAvgStrikePrice = createSelector(
  selectLoanStrikePrices,
  selectWeightedAvgTokensAmount,
  (strikePrices, summaryTokensAmount) => {
    if (summaryTokensAmount === 0) return 0;
    return strikePrices.reduce(
      (accum, item) => accum + (item.tokensAmount * item.strikePrice) / summaryTokensAmount,
      0,
    );
  },
);
